import React from 'react';
import { Box, Select, MenuItem, Typography, Tooltip, InputAdornment } from '@mui/material';
import { HiOutlineExclamationCircle as ErrorIcon } from 'react-icons/hi';

const RequiredMarker = () => (
    <Box
        component="span"
        sx={{ color: (theme) => theme.palette.error.main }}
    >
        *{' '}
    </Box>
);
const ErrorTooltip = ({ errorMessage }) => {
    return (
        <InputAdornment
            sx={(theme) => {
                return {
                    mr: theme.spacing(1),
                    cursor: 'pointer',
                };
            }}
        >
            <Tooltip
                title={errorMessage}
                placement="top"
                arrow
                sx={(theme) => {
                    return {
                        color: theme.palette.error.main,
                    };
                }}
            >
                <Box>
                    <ErrorIcon />
                </Box>
            </Tooltip>
        </InputAdornment>
    );
};

/** A styled wrapper for the MUI Select component. Uses a Typography with component of label instead of the default Select label.
 *
 * @param {boolean} required A boolean property that if present will add the required marker.
 * @param {string} label A text value to display as the label of the input.
 * @param {Array<Object>} children An Array of Objects with the following required properties.
 * @param {string|integer} children.id A string or integer value unique to the entry. Usually an integer id, but not always.
 * @param {string} children.label A string to display as the option.
 * @param {string} id A unique identifier for linking the label and input.
 * @param {string} errorMessage A message relative to the reason the component is in error. Business does not like the standard helper text, so this is in place of that.
 * @param {object|function} sx A MUI sx object or generator function to be applied to the parent Box component. Following the new convention, this can be used to override any of the children components. See FeesTable component.
 * @param {object|function} typographySxObj A MUI sx object or generator function to be applied to the Typography component.
 * @param {object|function} selectSxObj A MUI sx object  or generator function to be applied to the Select component.
 * @param {boolean} disabled A boolean value that is applyed to the MUI Select component and controls things like label interactivity and tabIndex override.
 * @param {object} inputProps A MUI inputProps object that will be applied to the MUI Select component.
 * @param {Any} restOfProps Any properties that are valid for the MUI Select component.
 * @see {@link https://mui.com/material-ui/api/box/ MUI Box API} for further information.
 * @see {@link https://mui.com/material-ui/api/typography/ MUI Typography API} for further information.
 * @see {@link https://mui.com/material-ui/api/select/ MUI Select API} for further information.
 * @see {@link https://mui.com/material-ui/api/menu-item/ MUI Menu Item API} for further information.
 */
const MuiStyledSelect = ({
    required,
    label,
    children,
    id,
    errorMessage,
    sx,
    typographySxObj,
    selectSxObj,
    disabled,
    inputProps,
    ...restOfProps
}) => {
    const [isOpen, setIsOpen] = React.useState(false);

    const handleLabelClick = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    const handleClose = () => {
        restOfProps?.onClose?.();
        setIsOpen(false);
    };

    return (
        <Box
            sx={sx}
        >
            <Typography
                component="div"
                sx={(theme) => {
                    let override = typeof typographySxObj === 'function' ? typographySxObj(theme) : typographySxObj;
                    return {
                        display: 'block',
                        color: restOfProps?.error ? theme.palette.error.main : theme.palette.grey.header,
                        fontWeight: 700,
                        fontSize: '0.8125rem',
                        ...override,
                    };
                }}
                onClick={handleLabelClick}
            >
                {required ? <RequiredMarker /> : null}
                {label}
            </Typography>

            <Select
                id={id}
                open={isOpen}
                {...restOfProps}
                onClose={handleClose}
                onOpen={() => setIsOpen(true)}
                size="small"
                autoWidth={false}
                sx={(theme) => {
                    let override = typeof selectSxObj === 'function' ? selectSxObj(theme) : selectSxObj;
                    return {
                        width: '14rem',
                        '&.Mui-disabled': {
                            backgroundColor: theme.palette.grey['disabled-background'],
                            color: '#586874',
                            fontSize: '15px',
                            '> .MuiSelect-select': {
                                WebkitTextFillColor: 'unset',
                            },
                            '> .MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                            },
                        },
                        [theme.breakpoints.down('xl')]: {
                            width: '10.9rem',
                        },
                        [theme.breakpoints.down('lg')]: {
                            width: '7.8rem',
                        },
                        ...override,
                    };
                }}
                inputProps={{
                    ...inputProps,
                    tabIndex: disabled ? '-1' : inputProps?.tabIndex,
                }}
                disabled={disabled}
                startAdornment={restOfProps?.error && errorMessage ? <ErrorTooltip errorMessage={errorMessage} /> : undefined}
            >
                {children.map((item) => {
                    return (
                        <MenuItem
                            key={`option-${id}-${item.id}`}
                            value={typeof item?.value !== 'undefined' ? item.value : item.id}
                            sx={{
                                height: '2.25rem',
                            }}
                            disabled={item?.disabled}
                        >
                            {item.label || ''}
                        </MenuItem>
                    );
                })}
            </Select>
        </Box>
    );
};

export default MuiStyledSelect;
